import {
  forwardRef,
  useId,
  useState,
  type HTMLInputAutoCompleteAttribute,
  type HTMLInputTypeAttribute,
} from 'react';
import { InputWrapper, type BaseInputProps } from './InputWrapper';
import { cn } from '~/utils';

type SelectInputProps = BaseInputProps & {
  type?: HTMLInputTypeAttribute;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  options: { label: string; value: string }[];
};

export const Select = forwardRef<HTMLSelectElement, SelectInputProps>(
  (
    {
      autoComplete,
      label,
      error,
      required,
      disabled,
      containerClassName,
      leftIcon,
      rightIcon,
      rightAlign,
      name,
      value,
      onChange,
      onFocus: onFocusCallback,
      onBlur: onBlurCallback,
      onKeyDown,
      dataCy,
      className,
      options,
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const id = useId();
    const errorId = `${id}-error`;
    const hasError = error !== undefined && error !== '';

    const onFocus = () => {
      setFocus(true);
      onFocusCallback?.();
    };

    const onBlur = () => {
      onBlurCallback?.();
      setFocus(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const name = e.target.name;
      const value = e.target.value;
      onChange?.(name, value);
    };

    return (
      <InputWrapper
        label={label}
        id={id}
        errorId={errorId}
        error={error}
        required={required}
        disabled={disabled}
        containerClassName={containerClassName}
        focus={focus}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
      >
        <select
          className={cn(
            'bg-none border-none focus:outline-none hover:outline-none py-3 px-4 text-sm leading-[100%] bg-neut-0 rounded font-normal w-full',
            rightAlign ? 'text-right' : '',
            className
          )}
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={hasError ? id : undefined}
          aria-required={required}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={onKeyDown}
          ref={ref}
          data-cy={dataCy}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </InputWrapper>
    );
  }
);

Select.displayName = 'Select';
